import clsx from "clsx";
import { X } from "lucide-react";
import { useSyncExternalStore } from "react";

type Tone = "info" | "success" | "warning" | "error";
/** Where a toast can send the user: a path inside the app. */
export type ToastLink = { label: string; to: string };
type Item = { id: number; message: string; tone: Tone; link?: ToastLink };

let items: Item[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

export const toastStore = {
  get: () => items,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
  dismiss(id: number) {
    items = items.filter((t) => t.id !== id);
    emit();
  },
  clear() {
    items = [];
    emit();
  },
};

export function toast(message: string, tone: Tone = "info", link?: ToastLink): void {
  const id = nextId++;
  items = [...items.slice(-3), { id, message, tone, link }];
  emit();
  if (tone !== "error") setTimeout(() => toastStore.dismiss(id), 6000);
}

const toneClass: Record<Tone, string> = {
  info: "border-line",
  success: "border-green",
  warning: "border-orange",
  error: "border-danger",
};

/**
 * `onNavigate` takes a toast's link inside the app (the shell passes the router's). Without it, and for a click
 * that opens a new tab, the link is a plain one.
 */
export function Toaster({ onNavigate }: { onNavigate?: (to: string) => void } = {}) {
  const list = useSyncExternalStore(toastStore.subscribe, toastStore.get, toastStore.get);
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed right-4 bottom-4 z-[60] grid w-[min(92vw,380px)] gap-2"
    >
      {list.map((t) => (
        <div
          key={t.id}
          className={clsx(
            "pointer-events-auto flex items-start gap-3 rounded-lg border-l-4 border bg-surface px-3 py-2.5 text-sm text-ink shadow-pop",
            toneClass[t.tone],
          )}
        >
          <p className="flex-1">
            {t.message}
            {t.link ? (
              <a
                href={t.link.to}
                onClick={(e) => {
                  if (!onNavigate || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  onNavigate(t.link?.to ?? "/");
                  toastStore.dismiss(t.id);
                }}
                className="ml-2 font-medium whitespace-nowrap text-green-ink underline"
              >
                {t.link.label}
              </a>
            ) : null}
          </p>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => toastStore.dismiss(t.id)}
            className="text-ink-3 hover:text-ink"
          >
            <X size={15} />
          </button>
        </div>
      ))}
    </div>
  );
}
